import { useEffect, useState } from "react"
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  CircularProgress,
  Box,
  Stack,
  Alert,
} from "@mui/material"
import { useTranslation } from "../../common/components/LocalizationProvider"
import { usePremiumTokens } from "../../common/theme/usePremiumTokens"
import { AuditLogItem } from "../types"

interface RecoveryPreview {
  canApply: boolean
  snapshot: Record<string, any>
  permissions: Record<string, any>
  conflicts: string[]
  restoredId?: number
}

interface RecoveryDialogProps {
  open: boolean
  action: AuditLogItem | null
  onClose: () => void
  onRestored: (message?: string) => void
  onError: (message: string) => void
}

const RecoveryDialog = ({ open, action, onClose, onRestored, onError }: RecoveryDialogProps) => {
  const t = useTranslation()
  const tokens = usePremiumTokens()
  const [loading, setLoading] = useState(false)
  const [preview, setPreview] = useState<RecoveryPreview | null>(null)
  const [error, setError] = useState<string | null>(null)

  const resetState = () => {
    setPreview(null)
    setError(null)
  }

  useEffect(() => {
    if (open && action) {
      fetchPreview(false)
    } else if (!open) {
      resetState()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, action?.id])

  // Premium code block styling with tokens
  const preStyle = {
    margin: 0,
    whiteSpace: "pre-wrap" as const,
    wordBreak: "break-word" as const,
    fontSize: "0.75rem",
    fontFamily: "monospace",
    backgroundColor: tokens.surface.card,
    border: `1px solid ${tokens.border.subtle}`,
    padding: "12px",
    borderRadius: tokens.radius.md,
    maxHeight: 220,
    overflow: "auto" as const,
    color: tokens.text.primary,
  }

  const renderPre = (payload: Record<string, any>) => (
    <pre style={preStyle}>
      {JSON.stringify(payload ?? {}, null, 2)}
    </pre>
  )

  const fetchPreview = async (apply: boolean) => {
    if (!action) {
      return
    }
    try {
      setLoading(true)
      setError(null)
      const response = await fetch("/api/reports/actions/recover", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ actionId: action.id, apply }),
      })
      if (!response.ok) {
        const message = await response.text()
        throw new Error(message || t("recoveryError"))
      }
      const data = (await response.json()) as RecoveryPreview
      setPreview(data)
      if (apply) {
        onRestored(t("recoverySuccess"))
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : t("recoveryUnexpectedError")
      setError(message)
      if (apply) {
        onError(message)
      }
    } finally {
      setLoading(false)
    }
  }

  const handleRestore = async () => {
    await fetchPreview(true)
  }

  const conflicts = preview?.conflicts ?? []
  const readyToRestore = preview && conflicts.length === 0 && preview.canApply

  // Premium dialog styling
  const dialogSx = {
    '& .MuiDialog-paper': {
      backgroundColor: tokens.surface.elevated,
      borderRadius: tokens.radius.lg,
    },
    '& .MuiDialogTitle-root': {
      backgroundColor: tokens.surface.card,
      borderBottom: `1px solid ${tokens.border.subtle}`,
      color: tokens.text.primary,
    },
    '& .MuiDialogContent-root': {
      backgroundColor: tokens.surface.elevated,
    },
    '& .MuiDialogActions-root': {
      backgroundColor: tokens.surface.card,
      borderTop: `1px solid ${tokens.border.subtle}`,
      padding: '12px 24px',
    },
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md" sx={dialogSx}>
      <DialogTitle>{t("recoveryDialogTitle")}</DialogTitle>
      <DialogContent dividers>
        {!preview && loading ? (
          <Box sx={{ py: 4, display: "flex", justifyContent: "center" }}>
            <CircularProgress />
          </Box>
        ) : (
          <Stack spacing={2}>
            {error && <Alert severity="error">{error}</Alert>}
            {preview && (
              <>
                <Alert severity={readyToRestore ? "success" : "warning"}>
                  {readyToRestore
                    ? t("recoveryNoConflicts")
                    : conflicts.length
                      ? t("recoveryHasConflicts")
                      : t("recoveryPendingConfirmation")}
                </Alert>
                {conflicts.length > 0 && (
                  <Box>
                    <Typography variant="subtitle2" gutterBottom>
                      {t("recoveryConflictsDetected")}
                    </Typography>
                    <Stack spacing={1}>
                      {conflicts.map((conflict) => (
                        <Alert key={conflict} severity="warning">
                          {conflict}
                        </Alert>
                      ))}
                    </Stack>
                  </Box>
                )}
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    {t("recoveryOriginalData")}
                  </Typography>
                  {renderPre(preview.snapshot ?? {})}
                </Box>
                {preview.permissions && Object.keys(preview.permissions).length > 0 && (
                  <Box>
                    <Typography variant="subtitle2" gutterBottom>
                      {t("recoveryAssociatedPermissions")}
                    </Typography>
                    {renderPre(preview.permissions)}
                  </Box>
                )}
              </>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          {t("sharedCancel")}
        </Button>
        <Button onClick={() => fetchPreview(false)} disabled={loading || !action}>
          {t("recoveryUpdatePreview")}
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleRestore}
          disabled={loading || !action || !readyToRestore}
        >
          {t("recoveryRestore")}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default RecoveryDialog
